// "A buyer wrote to us and nobody has answered."
//
// ── WHAT THIS FIXES ──────────────────────────────────────────────────────────
// Buyer replies relayed by GoHighLevel were filed as our own mail for two
// months (35 of them, "Could you tell me the lead time…"), and when that was
// fixed the signal was a flag — on an RFQ list where 230 RFQs are already
// flagged for unrelated reasons. A waiting buyer was still invisible.
//
// This reads rfqs.awaiting_reply_since: set by any customer reply, cleared the
// moment we email the buyer, send a quote, or someone presses "Mark answered".
// Oldest first, because whoever has waited longest is the most urgent.
//
// Same rules as accepted-awaiting-action.tsx: zero pixels when nobody is
// waiting (an always-present panel becomes furniture), and a failed fetch is
// LOUD, because "render nothing" must never be mistaken for "nobody waiting".

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { MessageCircleWarning, ArrowRight, ChevronDown } from 'lucide-react';
import {
  api,
  type BuyersAwaitingReplyResponse,
  type BuyerAwaitingReply,
  type MarkAnsweredResponse,
} from '@/lib/api';
import { timeAgo } from '@/lib/format';

export const BUYERS_WAITING_KEY = ['buyers-awaiting-reply'];

export function useBuyersWaiting() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: BUYERS_WAITING_KEY,
    queryFn: () =>
      api<BuyersAwaitingReplyResponse>('/api/rfqs/awaiting-reply', { tokenGetter: () => getToken() }),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkAnswered() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rfqId: string) =>
      api<MarkAnsweredResponse>(`/api/rfqs/${rfqId}/mark-answered`, {
        method: 'POST', tokenGetter: () => getToken(), body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BUYERS_WAITING_KEY });
      // The flag note may have changed too.
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      qc.invalidateQueries({ queryKey: ['rfq'] });
    },
  });
}

/** Oldest-first, so the preview is the people who have waited longest. */
const PREVIEW = { rail: 3, band: 5 } as const;

function ShowAllToggle({ total, shown, expanded, onToggle, className }: {
  total: number; shown: number; expanded: boolean; onToggle: () => void; className: string;
}) {
  if (total <= shown) return null;
  return (
    <button type="button" onClick={onToggle} aria-expanded={expanded}
      className={`flex w-full items-center justify-center gap-1 text-xs font-medium hover:bg-sky-100/60 ${className}`}>
      {expanded ? 'Show fewer' : `Show all ${total}`}
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
    </button>
  );
}

function waited(since: string | null): string {
  if (!since) return 'waiting';
  const days = Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000);
  return days >= 1 ? `waiting ${days} day${days === 1 ? '' : 's'}` : `waiting since ${timeAgo(since)}`;
}

/**
 * `band` — the dashboard treatment, with the buyer's last words and Mark answered.
 * `rail` — the narrow strip above the RFQ list; links only.
 */
export function BuyersWaitingPanel({ variant }: { variant: 'band' | 'rail' }) {
  const { data, isLoading, isError, error } = useBuyersWaiting();
  const mark = useMarkAnswered();
  const [expanded, setExpanded] = useState(false);

  if (isError) {
    return (
      <div
        className={variant === 'rail'
          ? 'px-4 py-2 border-b-2 border-amber-500/60 bg-amber-50 text-xs text-amber-900'
          : 'card mb-6 px-5 py-3 border-amber-500/50 text-xs text-amber-900 bg-amber-50'}
        role="alert"
      >
        <span className="font-semibold">Could not check for buyers waiting on a reply.</span>{' '}
        {(error as { message?: string })?.message ?? 'Unknown error'} — this panel is NOT saying
        nobody is waiting. Reload; if it persists, check the inbox directly.
      </div>
    );
  }

  if (isLoading || !data || data.buyers.length === 0) return null;
  const all = data.buyers;
  const preview = PREVIEW[variant];
  const items = expanded ? all : all.slice(0, preview);

  if (variant === 'rail') {
    return (
      <div className="border-b-2 border-sky-500/60 bg-sky-50">
        <div className="px-4 py-2 flex items-center gap-2 text-xs font-semibold text-sky-900">
          <MessageCircleWarning className="w-3.5 h-3.5 shrink-0" />
          {all.length} {all.length === 1 ? 'buyer' : 'buyers'} waiting on a reply
        </div>
        <ul>
          {items.map((b) => (
            <li key={b.rfqId}>
              <Link
                href={`/rfqs/${b.rfqId}`}
                className="block px-4 py-2 border-t border-sky-200/60 hover:bg-sky-100/60 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-xs font-semibold">{b.customerLegalName ?? 'Unknown buyer'}</span>
                  {b.isAog && <span className="pill pill-danger font-semibold shrink-0">AOG</span>}
                </div>
                <div className="truncate text-xs text-sky-800">{b.rfqRef} · {waited(b.awaitingSince)}</div>
              </Link>
            </li>
          ))}
        </ul>
        <ShowAllToggle total={all.length} shown={preview} expanded={expanded} onToggle={() => setExpanded((v) => !v)}
          className="h-10 border-t border-sky-200/60 text-sky-900" />
      </div>
    );
  }

  return (
    <section className="card overflow-hidden mb-6 border-sky-500/50 ring-1 ring-sky-500/20">
      <header className="px-4 py-3 sm:px-5 border-b border-border bg-sky-50">
        <h2 className="font-semibold text-sm flex items-center gap-2 text-sky-900">
          <MessageCircleWarning className="w-4 h-4" />
          {all.length} {all.length === 1 ? 'buyer is' : 'buyers are'} waiting on a reply
        </h2>
      </header>
      {mark.isError && (
        <div className="px-5 py-2 text-xs text-danger border-b border-border" role="alert">
          Could not mark answered: {(mark.error as { message?: string })?.message ?? 'Unknown error'}.
        </div>
      )}
      <ul className="divide-y divide-border">
        {items.map((b) => (
          <BuyerRow
            key={b.rfqId}
            b={b}
            onMarkAnswered={() => mark.mutate(b.rfqId)}
            marking={mark.isPending && mark.variables === b.rfqId}
          />
        ))}
      </ul>
      <ShowAllToggle total={all.length} shown={preview} expanded={expanded} onToggle={() => setExpanded((v) => !v)}
        className="h-11 border-t border-border text-sky-900" />
      {data.truncated && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border">
          Showing the 50 who have waited longest.
        </div>
      )}
    </section>
  );
}

function BuyerRow({ b, onMarkAnswered, marking }: {
  b: BuyerAwaitingReply; onMarkAnswered: () => void; marking: boolean;
}) {
  // Stacked on a phone, side by side from sm up. The text column must not be
  // `flex-1` inside a wrapping row: its basis is 0, so it never wraps the buttons
  // underneath and gets squeezed to a few letters wide instead.
  return (
    <li className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-5">
      <div className="min-w-0 sm:flex-1">
        <div className="flex items-start justify-between gap-2 sm:justify-start sm:gap-2">
          <span className="min-w-0 break-words text-sm font-semibold [overflow-wrap:anywhere]">
            {b.customerLegalName ?? 'Unknown buyer'}
          </span>
          {b.isAog && <span className="pill pill-danger shrink-0 font-semibold">AOG</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
          <span className="font-mono text-muted-foreground">{b.rfqRef}</span>
          <span className="font-medium text-sky-800">{waited(b.awaitingSince)}</span>
        </div>
        {b.lastMessageExcerpt && (
          <div className="mt-1.5 line-clamp-2 break-words text-xs text-foreground">“{b.lastMessageExcerpt}”</div>
        )}
        {b.contactEmail && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {b.contactFullName ? `${b.contactFullName} · ` : ''}{b.contactEmail}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:items-center">
        <button
          type="button"
          onClick={onMarkAnswered}
          disabled={marking}
          className="btn-outline h-10 justify-center text-xs sm:h-auto"
          title="Answered outside the app (phone, WhatsApp, a Gmail reply the app did not see)."
        >
          {marking ? 'Saving…' : 'Mark answered'}
        </button>
        <Link href={`/rfqs/${b.rfqId}`} className="btn-primary h-10 justify-center text-xs sm:h-auto">
          Open RFQ <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </li>
  );
}
